import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wrench, Search } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const LEVELS = ['Hobby', 'Prosumer', 'Professional', 'Industrial'];

const CuttingToolsDatabase = () => {
  const [params, setParams] = useSearchParams();
  const [tools, setTools] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  const query = params.get('query') || '';
  const category = params.get('category') || '';
  const material = params.get('material') || '';
  const operation = params.get('operation') || '';
  const level = params.get('level') || '';

  useEffect(() => {
    const load = async () => {
      try {
        const url = query || category || material || operation || level ? `${API}/tools/search` : `${API}/tools`;
        const res = await axios.get(url, {
          params: {
            query: query || undefined,
            category: category || undefined,
            material: material || undefined,
            operation: operation || undefined,
            level: level || undefined,
          },
        });
        setTools(res.data || []);

        // Machine context (for compatibility messaging)
        const m = await axios.get(`${API}/machines`).catch(() => ({ data: [] }));
        setMachines(m.data || []);
      } catch (e) {
        console.error('Failed to load cutting tools database', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [query, category, material, operation, level]);

  const facets = useMemo(() => {
    const categories = new Set();
    const materials = new Set();
    const operations = new Set();
    const levels = new Set();

    for (const t of tools) {
      if (t.category) categories.add(t.category);
      if (t.level) levels.add(t.level);
      (t.materials || []).forEach((m) => materials.add(m));
      (t.operation_types || []).forEach((o) => operations.add(o));
    }

    return {
      categories: Array.from(categories).sort(),
      materials: Array.from(materials).sort(),
      operations: Array.from(operations).sort(),
      levels: Array.from(levels).sort(),
    };
  }, [tools]);

  const machine = useMemo(() => {
    // Prefer first machine that is complete under the “B” definition.
    const required = ['axis_count', 'motor_class', 'spindle_type_manual', 'controller_board', 'rigidity'];
    const isSet = (v) => typeof v === 'string' && v.trim() !== '' && v.toLowerCase() !== 'unknown';
    return (
      machines.find((m) => required.every((f) => isSet(m?.[f] || ''))) ||
      machines[0] ||
      null
    );
  }, [machines]);

  const getCompatibility = (tool) => {
    if (!machine) return null;

    const spindle = machine.spindle_type_manual || machine.spindle_type_inferred || 'unknown';
    const machineMaxRpm = typeof machine.max_rpm === 'number' ? machine.max_rpm : null;
    const toolRpmMin = tool.recommended_rpm_range?.min ?? null;

    const maxDiaAllowed = (() => {
      if (spindle === 'trim_router') return 8; // conservative (1/4" class)
      if (spindle === 'small_water_cooled') return 12;
      if (spindle === 'medium_spindle') return 16;
      if (spindle === 'high_power_spindle') return 25;
      return 8;
    })();

    const minDiameter = Math.min(...(tool.diameters_mm || [999]));
    const tooLarge = minDiameter > maxDiaAllowed;

    const requiresHighRigidity = (tool.tags || []).includes('high_rigidity');
    const rigidity = (machine.rigidity || 'unknown').toLowerCase();
    const lowRigidity = rigidity === 'light' || rigidity === 'unknown';

    const rpmMismatch = machineMaxRpm && toolRpmMin ? machineMaxRpm < toolRpmMin : false;

    if (tooLarge) {
      return { status: 'hide', message: `Not suitable: tool sizes start above ${maxDiaAllowed}mm for your spindle.` };
    }

    if ((requiresHighRigidity && lowRigidity) || rpmMismatch) {
      const reasons = [];
      if (requiresHighRigidity && lowRigidity) reasons.push('requires higher rigidity');
      if (rpmMismatch) reasons.push(`needs ≥${toolRpmMin} RPM`);
      return { status: 'warn', message: `⚠ ${reasons.join(' and ')} than your machine supports.` };
    }

    return { status: 'ok', message: '✔ Suitable for your machine' };
  };

  const visibleTools = useMemo(() => {
    return tools.filter((t) => {
      const c = getCompatibility(t);
      return c?.status !== 'hide';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tools, machine]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.ROUTER_CUTTING_TOOLS)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="cutting-tools-db">
      <div className="panel-header">
        <div>
          <div className="panel-title">Knowledge Base / Cutting Tools Database</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Cutting Tools Database</h1>
          <p className="text-sm md:text-base text-[var(--text-secondary)] mt-2 max-w-3xl">
            Search and filter common CNC router tools by operation, material, category, and machine level.
          </p>
        </div>
      </div>

      <Card className="bg-black/60 border-[#3a3a3a]">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Search className="w-4 h-4" />
            Search & Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Input
                value={query}
                onChange={(e) => updateParam('query', e.target.value)}
                placeholder="Search by name, tag, material, operation…"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <Select value={operation} onValueChange={(v) => updateParam('operation', v === 'all' ? '' : v)}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Operation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All operations</SelectItem>
                {facets.operations.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={material} onValueChange={(v) => updateParam('material', v === 'all' ? '' : v)}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Material" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All materials</SelectItem>
                {facets.materials.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={category} onValueChange={(v) => updateParam('category', v === 'all' ? '' : v)}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Tool category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {facets.categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select value={level} onValueChange={(v) => updateParam('level', v === 'all' ? '' : v)}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Machine level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                {LEVELS.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="md:col-span-2 flex gap-2">
              <Button
                variant="outline"
                className="border-slate-700 text-slate-200"
                onClick={() => setParams(new URLSearchParams())}
              >
                Clear filters
              </Button>
              <Button variant="outline" className="border-slate-700 text-slate-200" asChild>
                <Link to="/knowledge">Back to Knowledge Base</Link>
              </Button>
            </div>
          </div>

          {machine && (
            <div className="text-xs text-[var(--text-secondary)]">
              Compatibility is based on your machine profile: <span className="text-slate-200">{machine.name || 'Machine'}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && (
          <div className="text-sm text-[var(--text-secondary)]">Loading tools…</div>
        )}

        {!loading && visibleTools.length === 0 && (
          <div className="text-sm text-[var(--text-secondary)]">No tools found for your search.</div>
        )}

        {visibleTools.map((tool) => {
          const compat = getCompatibility(tool);
          const short = tool.descriptions?.usage || '';
          const dia = (tool.diameters_mm || []).join(', ');
          const fl = (tool.flute_count || []).join(', ');

          return (
            <Card key={tool.id} className="bg-black/60 border-[#3a3a3a]">
              <CardHeader>
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-amber-300" />
                  {tool.name}
                </CardTitle>
                <div className="text-xs text-[var(--text-secondary)]">
                  {tool.category} · {tool.level}
                </div>
                {compat && (
                  <div className="text-xs mt-2">
                    <span className={compat.status === 'ok' ? 'text-emerald-300' : 'text-amber-300'}>
                      {compat.message}
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-[var(--text-secondary)] line-clamp-3">{short}</div>

                <div className="text-xs text-slate-200">
                  <div>Diameters (mm): {dia || '—'}</div>
                  <div>Flutes: {fl || '—'}</div>
                  <div>Materials: {(tool.materials || []).slice(0, 3).join(', ')}{(tool.materials || []).length > 3 ? '…' : ''}</div>
                </div>

                <Button size="sm" className="btn btn-primary" asChild>
                  <Link to={`/knowledge/tools/${tool.id}`}>View Details</Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
    </div>
  );
};

export default CuttingToolsDatabase;
